import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Button, Icon, IconButton } from '@/components/common';
import { cn } from '@/utils/cn';

import { useConflictReviewStore } from '../conflict-review-store';
import type { ProjectScope } from '../project-scope';
import { resolveProjectWriteConflict, type ResolveProjectWriteConflictAction } from '../project-write-authority-api';
import * as projectWriteOutbox from '../project-write-outbox';
import type { ProjectWriteOutboxEntry } from '../project-write-outbox';
import type { ProjectWriteOperation } from '../write-operation';

type ReviewStatus = 'conflict_review' | 'retryable' | 'rejected';

type ReviewItem = ProjectWriteOutboxEntry & {
    status: ReviewStatus;
};

type DetailRow = {
    label: string;
    value: string;
};

type OperationReviewSummary = {
    title: string;
    targetLabel: string;
    reasonLabel: string;
    detailRows: DetailRow[];
};

type ConflictReviewPanelProps = {
    isOpen: boolean;
    onClose: () => void;
    scope: ProjectScope | null | undefined;
};

const featureLabels: Record<ProjectWriteOperation['feature'], string> = {
    setup_access: 'Setup / Access',
    script_show: 'Script / Show',
    people_casting: 'People / Casting',
    notifications_email: 'Notifications / Email',
    schedule: 'Schedule',
    notes_reports: 'Notes / Reports',
    files: 'Files',
    blocking_choreography: 'Blocking / Choreography',
    attendance: 'Attendance',
    action_items: 'Action Items',
    department_tracking: 'Department Tracking',
    performance: 'Performance',
    admin_adjacent: 'Admin / Adjacent',
};

const statusConfig: Record<ReviewStatus, {
    icon: string;
    label: string;
    className: string;
}> = {
    conflict_review: {
        icon: 'rule',
        label: 'Needs review',
        className: 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200',
    },
    retryable: {
        icon: 'sync_problem',
        label: 'Needs retry',
        className: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-200',
    },
    rejected: {
        icon: 'error',
        label: 'Could not apply',
        className: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-200',
    },
};

const reasonLabels: Record<string, string> = {
    'unsafe-ordered-list-merge': 'The server cannot safely merge this saved order with the shared order.',
    'same-conflict-unit': 'Another saved write touched the same project area.',
    'suspicious-clock-skew': 'This device clock looks out of sync, so the server paused the write.',
    'stale-side-effect-command': 'The command target changed before this command could run.',
    'missing-automatic-resolver': 'This type of saved change does not have a safe automatic merge yet.',
    'storage-metadata-mismatch': 'The uploaded file metadata does not match the saved change.',
    'missing-conflict-apply-resolver': 'The server cannot apply this conflict automatically yet.',
    'missing-conflict-id': 'This saved write is missing the server conflict record needed to resolve it.',
    'missing-capability': 'The server cannot apply this saved change yet.',
};

function isReviewStatus(status: ProjectWriteOutboxEntry['status']): status is ReviewStatus {
    return status === 'conflict_review' || status === 'retryable' || status === 'rejected';
}

function toReasonLabel(item: ReviewItem): string {
    return item.reason ?? item.lastResult?.reason ?? (
        item.status === 'rejected'
            ? 'The server could not apply this saved change.'
            : item.status === 'retryable'
                ? 'Waiting for auth, network, or server recovery'
                : 'Manual decision required'
    );
}

function isStaleSideEffectCommand(item: ReviewItem): boolean {
    return item.operation.kind === 'sideEffectCommand'
        && toReasonLabel(item) === 'stale-side-effect-command';
}

function conflictIdFor(item: ReviewItem): string | null {
    return item.conflictId ?? item.lastResult?.conflictId ?? null;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function humanizeToken(value: string): string {
    const normalized = value
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[._-]+/g, ' ')
        .trim();

    if (!normalized) {
        return value;
    }

    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function humanizePath(value: string): string {
    return value.split('/').map(humanizeToken).join(' / ');
}

function lowerFirst(value: string): string {
    if (!value) {
        return value;
    }

    return value.charAt(0).toLowerCase() + value.slice(1);
}

function operationKindLabel(kind: ProjectWriteOperation['kind']): string {
    const humanized = humanizeToken(kind).toLowerCase();
    return humanized.charAt(0).toUpperCase() + humanized.slice(1);
}

function truncateText(value: string, maxLength = 120): string {
    return value.length > maxLength ? `${value.slice(0, maxLength - 3)}...` : value;
}

function formatCreatedAt(value: number): string | null {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return date.toISOString();
}

function formatValue(value: unknown): string {
    if (value === null) {
        return 'None';
    }

    if (value === undefined) {
        return 'Not provided';
    }

    if (typeof value === 'string') {
        return value.trim() ? truncateText(value) : 'Blank';
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }

    if (Array.isArray(value)) {
        if (value.length === 0) {
            return 'None';
        }

        const primitiveItems = value.filter((item) => (
            typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean'
        ));
        if (primitiveItems.length === value.length && value.length <= 4) {
            return value.map((item) => String(item)).join(', ');
        }

        return `${value.length} ${value.length === 1 ? 'item' : 'items'}`;
    }

    if (isPlainObject(value)) {
        const entries = Object.keys(value).length;
        return entries === 0 ? 'Empty object' : `${entries} ${entries === 1 ? 'field' : 'fields'}`;
    }

    if (typeof value === 'bigint') {
        return value.toString();
    }

    if (typeof value === 'symbol') {
        return value.description ? `Symbol(${value.description})` : 'Symbol';
    }

    return 'Unsupported value';
}

function rowsFromPayloadObject(payload: Record<string, unknown>, hiddenKeys: readonly string[] = []): DetailRow[] {
    const hidden = new Set(hiddenKeys);
    return Object.entries(payload)
        .filter(([key]) => !hidden.has(key))
        .slice(0, 6)
        .map(([key, value]) => ({
            label: humanizePath(key),
            value: formatValue(value),
        }));
}

function showStructureRows(value: unknown): DetailRow[] {
    if (!isPlainObject(value)) {
        return [];
    }

    return ['acts', 'scenes', 'characters']
        .filter((key) => Array.isArray(value[key]))
        .map((key) => {
            const items = value[key] as unknown[];
            return {
                label: humanizePath(key),
                value: `${items.length} ${items.length === 1 ? 'item' : 'items'}`,
            };
        });
}

function detailRowsForOperation(operation: ProjectWriteOperation): DetailRow[] {
    const payload = operation.payload;

    if (operation.kind === 'setField' && isPlainObject(payload)) {
        const fieldLabel = humanizePath(operation.conflictUnit);
        return [{ label: fieldLabel, value: formatValue(payload.value) }];
    }

    if (operation.kind === 'deleteEntity') {
        const recordId = isPlainObject(payload) ? payload.id : undefined;
        return [{ label: 'Record', value: formatValue(recordId ?? operation.conflictUnit.split('/').at(-1)) }];
    }

    if (operation.kind === 'replaceArtifact' && isPlainObject(payload)) {
        return rowsFromPayloadObject(payload, ['storagePath']).concat(
            typeof payload.storagePath === 'string'
                ? [{ label: 'Storage path', value: truncateText(payload.storagePath, 80) }]
                : [],
        );
    }

    if (operation.kind === 'sideEffectCommand' && isPlainObject(payload)) {
        return rowsFromPayloadObject(payload, ['payload', 'staleTargetValidation']).concat(
            isPlainObject(payload.payload)
                ? rowsFromPayloadObject(payload.payload).slice(0, 3)
                : [],
        );
    }

    if (operation.kind === 'orderedListCommand' && isPlainObject(payload)) {
        const showStructureDetails = showStructureRows(payload.showStructure);
        return rowsFromPayloadObject(payload, ['showStructure']).concat(showStructureDetails);
    }

    if (isPlainObject(payload)) {
        return rowsFromPayloadObject(payload);
    }

    return [{ label: 'Saved value', value: formatValue(payload) }];
}

function safeRejectedRowsForOperation(operation: ProjectWriteOperation): DetailRow[] {
    const createdAt = formatCreatedAt(operation.createdAtMs);
    return [
        { label: 'Operation', value: operationKindLabel(operation.kind) },
        { label: 'Conflict area', value: humanizePath(operation.conflictUnit) },
        ...(createdAt ? [{ label: 'Created', value: createdAt }] : []),
    ];
}

function titleForOperation(operation: ProjectWriteOperation, detailRows: DetailRow[]): string {
    const target = lowerFirst(humanizePath(operation.conflictUnit));

    if (operation.kind === 'setField') {
        return `Update ${lowerFirst(detailRows[0]?.label ?? humanizePath(operation.conflictUnit))}`;
    }

    if (operation.kind === 'patchEntity') {
        return `Update ${target}`;
    }

    if (operation.kind === 'deleteEntity') {
        return `Delete ${target}`;
    }

    if (operation.kind === 'replaceArtifact') {
        const artifactType = isPlainObject(operation.payload) && typeof operation.payload.artifactType === 'string'
            ? lowerFirst(humanizePath(operation.payload.artifactType))
            : 'artifact';
        return `Replace ${artifactType}`;
    }

    if (operation.kind === 'sideEffectCommand') {
        const commandType = isPlainObject(operation.payload) && typeof operation.payload.commandType === 'string'
            ? lowerFirst(humanizePath(operation.payload.commandType))
            : 'side-effect';
        return `Review ${commandType} command`;
    }

    if (operation.kind === 'orderedListCommand') {
        return `Review ordered change for ${target}`;
    }

    return `Review ${target}`;
}

function summaryForItem(item: ReviewItem): OperationReviewSummary {
    const reason = toReasonLabel(item);
    if (item.status === 'rejected') {
        const operationKind = operationKindLabel(item.operation.kind);
        return {
            title: `Saved ${lowerFirst(operationKind)} write`,
            targetLabel: humanizePath(item.operation.conflictUnit),
            reasonLabel: reasonLabels[reason] ?? reason,
            detailRows: safeRejectedRowsForOperation(item.operation),
        };
    }

    const detailRows = detailRowsForOperation(item.operation);
    return {
        title: titleForOperation(item.operation, detailRows),
        targetLabel: humanizePath(item.operation.conflictUnit),
        reasonLabel: reasonLabels[reason] ?? reason,
        detailRows,
    };
}

function projectLabel(scope: ProjectScope): string {
    return `${scope.orgId} / ${scope.projectId}`;
}

function groupItems(items: ReviewItem[]): Array<{
    key: string;
    project: ProjectScope;
    feature: ProjectWriteOperation['feature'];
    items: ReviewItem[];
}> {
    const groups = new Map<string, {
        key: string;
        project: ProjectScope;
        feature: ProjectWriteOperation['feature'];
        items: ReviewItem[];
    }>();

    for (const item of items) {
        const { scope, feature } = item.operation;
        const key = `${scope.orgId}/${scope.projectId}/${feature}`;
        const existing = groups.get(key);

        if (existing) {
            existing.items.push(item);
            continue;
        }

        groups.set(key, {
            key,
            project: scope,
            feature,
            items: [item],
        });
    }

    return [...groups.values()];
}

export const ConflictReviewPanel: React.FC<ConflictReviewPanelProps> = ({
    isOpen,
    onClose,
    scope,
}) => {
    const loadVersionRef = React.useRef(0);
    const latestScopeKeyRef = React.useRef<string | null>(null);
    const [items, setItems] = useState<ReviewItem[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [actingOperationId, setActingOperationId] = useState<string | null>(null);
    const refreshCounts = useConflictReviewStore((state) => state.refresh);
    const activeScope = useMemo(() => (
        scope?.orgId && scope.projectId
            ? { orgId: scope.orgId, projectId: scope.projectId }
            : null
    ), [scope?.orgId, scope?.projectId]);
    const activeScopeKey = activeScope ? projectLabel(activeScope) : null;
    latestScopeKeyRef.current = activeScopeKey;

    const itemMatchesActiveScope = useCallback((item: ReviewItem): boolean => (
        Boolean(activeScope)
        && item.operation.scope.orgId === activeScope?.orgId
        && item.operation.scope.projectId === activeScope.projectId
    ), [activeScope]);

    const loadItems = useCallback(async () => {
        const requestVersion = loadVersionRef.current + 1;
        loadVersionRef.current = requestVersion;

        if (!activeScope) {
            setItems([]);
            setErrorMessage(null);
            setActingOperationId(null);
            setIsLoading(false);
            return;
        }

        const scopeKey = projectLabel(activeScope);
        setIsLoading(true);
        setErrorMessage(null);
        try {
            const entries = await Promise.all([
                projectWriteOutbox.listProjectWriteReviewEntries(activeScope),
            ]);
            if (loadVersionRef.current !== requestVersion || latestScopeKeyRef.current !== scopeKey) {
                return;
            }

            setItems(entries.flat().filter((entry): entry is ReviewItem => isReviewStatus(entry.status)));
            await refreshCounts(activeScope);
        } catch (error) {
            if (loadVersionRef.current === requestVersion && latestScopeKeyRef.current === scopeKey) {
                setErrorMessage(error instanceof Error ? error.message : 'Unable to load preserved writes.');
            }
        } finally {
            if (loadVersionRef.current === requestVersion && latestScopeKeyRef.current === scopeKey) {
                setIsLoading(false);
            }
        }
    }, [activeScope, refreshCounts]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        void loadItems();
    }, [isOpen, loadItems]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: activeScopeKey is a render-only trigger that resets review state on scope changes.
    useEffect(() => {
        setItems([]);
        setErrorMessage(null);
        setActingOperationId(null);
        setIsLoading(false);
    }, [activeScopeKey]);

    const runAction = useCallback(async (
        operationId: string,
        item: ReviewItem,
        action: () => Promise<void>,
    ) => {
        if (!itemMatchesActiveScope(item)) {
            setErrorMessage('Project changed before this preserved write could be updated.');
            return;
        }

        setActingOperationId(operationId);
        setErrorMessage(null);
        try {
            await action();
            await loadItems();
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Unable to update preserved write.');
        } finally {
            setActingOperationId(null);
        }
    }, [itemMatchesActiveScope, loadItems]);

    const resolveConflict = useCallback(async (
        item: ReviewItem,
        action: ResolveProjectWriteConflictAction,
    ) => {
        if (!activeScope) {
            return;
        }

        const conflictId = conflictIdFor(item);
        if (!conflictId) {
            await projectWriteOutbox.markProjectWriteResult({
                operationId: item.operation.id,
                status: 'rejected',
                reason: 'missing-conflict-id',
            });
            return;
        }

        const response = await resolveProjectWriteConflict({
            orgId: activeScope.orgId,
            projectId: activeScope.projectId,
            conflictId,
            action,
            operation: item.operation,
            conflictReason: item.reason ?? item.lastResult?.reason,
        });
        await projectWriteOutbox.markProjectWriteResult(response.result);
    }, [activeScope]);

    const groupedItems = useMemo(() => groupItems(items), [items]);

    if (!isOpen) {
        return null;
    }

    return (
        <aside
            aria-label="Preserved writes review"
            className="fixed right-3 top-28 z-40 flex max-h-[calc(100vh-8rem)] w-[min(28rem,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl dark:border-border-dark dark:bg-surface-dark"
            data-testid="conflict-review-panel"
            role="dialog"
        >
            <header className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-border-dark">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <Icon name="rule" className="text-amber-600 dark:text-amber-300" />
                        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            Preserved writes need review
                        </h2>
                    </div>
                    <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                        These changes are saved locally, but they have not been applied to the shared project.
                    </p>
                </div>
                <IconButton
                    icon="close"
                    label="Close preserved writes review"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-white/5 dark:hover:text-gray-100"
                    onClick={onClose}
                />
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
                {isLoading ? (
                    <div className="flex items-center gap-2 py-6 text-sm text-gray-600 dark:text-gray-400">
                        <Icon name="progress_activity" className="animate-spin" />
                        Loading preserved writes
                    </div>
                ) : errorMessage ? (
                    <div
                        className="rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-200"
                        role="alert"
                    >
                        {errorMessage}
                    </div>
                ) : groupedItems.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-gray-300 px-3 py-5 text-sm text-gray-600 dark:border-border-dark dark:text-gray-400">
                        No preserved writes need review for this project.
                    </div>
                ) : (
                    <div className="space-y-4">
                        {groupedItems.map((group) => (
                            <section key={group.key} className="space-y-2">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div>
                                        <h3 className="text-xs font-semibold uppercase text-gray-700 dark:text-gray-300">
                                            {featureLabels[group.feature]}
                                        </h3>
                                        <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                            Project {projectLabel(group.project)}
                                        </p>
                                    </div>
                                    <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                                        {group.items.length} {group.items.length === 1 ? 'write' : 'writes'}
                                    </span>
                                </div>

                                <div className="space-y-2">
                                    {group.items.map((item) => {
                                        const config = statusConfig[item.status];
                                        const isActing = actingOperationId === item.operation.id;
                                        const isDismissibleStaleSideEffect = item.status === 'conflict_review' && isStaleSideEffectCommand(item);
                                        const summary = summaryForItem(item);

                                        return (
                                            <article
                                                className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 dark:border-border-dark dark:bg-white/5"
                                                key={item.operation.id}
                                            >
                                                <div className="flex flex-wrap items-start justify-between gap-2">
                                                    <div className="min-w-0">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <span className={cn(
                                                                'inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold',
                                                                config.className,
                                                            )}>
                                                                <Icon name={config.icon} size="xs" />
                                                                {config.label}
                                                            </span>
                                                            <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">
                                                                {summary.title}
                                                            </span>
                                                        </div>
                                                        {item.status !== 'rejected' ? (
                                                            <dl className="mt-2 grid gap-1 text-xs text-gray-600 dark:text-gray-400">
                                                                <div className="flex gap-1">
                                                                    <dt className="font-medium text-gray-700 dark:text-gray-300">Target:</dt>
                                                                    <dd>
                                                                        {summary.targetLabel}
                                                                        <span className="sr-only">
                                                                            {' '}
                                                                            {item.operation.conflictUnit}
                                                                        </span>
                                                                    </dd>
                                                                </div>
                                                                <div className="flex gap-1">
                                                                    <dt className="font-medium text-gray-700 dark:text-gray-300">Actor:</dt>
                                                                    <dd>{item.operation.actorId || 'Unknown actor'}</dd>
                                                                </div>
                                                            </dl>
                                                        ) : null}
                                                    </div>
                                                </div>

                                                <div className="mt-3 space-y-2">
                                                    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 dark:border-border-dark dark:bg-surface-dark/60">
                                                        <p className="text-[11px] font-semibold uppercase text-gray-500 dark:text-gray-400">
                                                            {item.status === 'rejected' ? 'Preserved write summary' : 'Proposed change'}
                                                        </p>
                                                        <dl className="mt-2 grid gap-1 text-xs text-gray-700 dark:text-gray-300">
                                                            {summary.detailRows.map((row) => (
                                                                <div className="grid grid-cols-[minmax(5rem,40%)_1fr] gap-2" key={`${row.label}:${row.value}`}>
                                                                    <dt className="min-w-0 wrap-break-word font-medium">
                                                                        {row.label}
                                                                    </dt>
                                                                    <dd className="min-w-0 wrap-break-word">
                                                                        {row.value}
                                                                    </dd>
                                                                </div>
                                                            ))}
                                                        </dl>
                                                    </div>

                                                    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs dark:border-amber-900/40 dark:bg-amber-900/20">
                                                        <p className="font-semibold text-amber-900 dark:text-amber-100">
                                                            Why this needs review
                                                        </p>
                                                        <p className="mt-1 text-amber-800 dark:text-amber-200">
                                                            {summary.reasonLabel}
                                                        </p>
                                                        <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-200/80">
                                                            Reason code:{' '}
                                                            <code>{toReasonLabel(item)}</code>
                                                        </p>
                                                    </div>

                                                    {item.status === 'conflict_review' && !isDismissibleStaleSideEffect ? (
                                                        <div className="grid gap-1 text-xs text-gray-600 dark:text-gray-400">
                                                            <p>
                                                                <span className="font-medium text-gray-700 dark:text-gray-300">Apply proposed:</span>
                                                                {' '}
                                                                Applies this saved change to the shared project.
                                                            </p>
                                                            <p>
                                                                <span className="font-medium text-gray-700 dark:text-gray-300">Keep shared:</span>
                                                                {' '}
                                                                Declines this saved change and leaves the shared project unchanged.
                                                            </p>
                                                        </div>
                                                    ) : null}
                                                    {item.status === 'rejected' ? (
                                                        <p className="text-xs text-gray-600 dark:text-gray-400">
                                                            This preserved write is saved for support review. Dismiss it only after confirming the shared project does not need this change.
                                                        </p>
                                                    ) : null}
                                                </div>

                                                <div className="mt-3 flex flex-wrap gap-2">
                                                    {item.status === 'conflict_review' && !isDismissibleStaleSideEffect ? (
                                                        <>
                                                            <Button
                                                                disabled={isActing}
                                                                isLoading={isActing}
                                                                onClick={() => {
                                                                    void runAction(item.operation.id, item, () => resolveConflict(item, 'apply_proposed'));
                                                                }}
                                                                size="sm"
                                                                variant="primary"
                                                            >
                                                                Apply proposed
                                                            </Button>
                                                            <Button
                                                                disabled={isActing}
                                                                onClick={() => {
                                                                    void runAction(item.operation.id, item, () => resolveConflict(item, 'keep_shared'));
                                                                }}
                                                                size="sm"
                                                                variant="secondary"
                                                            >
                                                                Keep shared
                                                            </Button>
                                                        </>
                                                    ) : null}

                                                    {item.status === 'retryable' ? (
                                                        <Button
                                                            disabled={isActing}
                                                            isLoading={isActing}
                                                            onClick={() => {
                                                                void runAction(item.operation.id, item, () => projectWriteOutbox.requeueProjectWrite(item.operation.id));
                                                            }}
                                                            size="sm"
                                                            variant="primary"
                                                        >
                                                            Retry
                                                        </Button>
                                                    ) : null}

                                                    {isDismissibleStaleSideEffect ? (
                                                        <Button
                                                            disabled={isActing}
                                                            onClick={() => {
                                                                void runAction(item.operation.id, item, () => resolveConflict(item, 'dismiss'));
                                                            }}
                                                            size="sm"
                                                            variant="secondary"
                                                        >
                                                            Dismiss
                                                        </Button>
                                                    ) : null}

                                                    {item.status === 'rejected' ? (
                                                        <Button
                                                            disabled={isActing}
                                                            onClick={() => {
                                                                void runAction(
                                                                    item.operation.id,
                                                                    item,
                                                                    () => projectWriteOutbox.supersedeProjectWrite(
                                                                        item.operation.id,
                                                                        'dismissed-rejected-write',
                                                                        ['rejected'],
                                                                    ),
                                                                );
                                                            }}
                                                            size="sm"
                                                            variant="secondary"
                                                        >
                                                            Dismiss
                                                        </Button>
                                                    ) : null}

                                                </div>
                                            </article>
                                        );
                                    })}
                                </div>
                            </section>
                        ))}
                    </div>
                )}
            </div>
        </aside>
    );
};
